import { Loc } from '../engine/core/localization.js';

// The backend lives on the same origin as the viewer, so a plain path is
// enough and the session cookie travels on its own. Nothing here reads or
// writes that cookie: it is HttpOnly and invisible to scripts on purpose.
const ApiPrefix = '/api';

export class ApiError extends Error
{
    constructor (code, message, details)
    {
        super (message);
        this.name = 'ApiError';
        this.code = code;
        // Whatever else the backend said about this refusal. An interrupted
        // upload, for instance, is answered with the offset to continue from.
        this.details = details || {};
    }
}

function UnavailableError ()
{
    return new ApiError ('unavailable', Loc ('The server is not responding. Try again later.'));
}

function ErrorFromPayload (payload)
{
    let error = (payload !== null && payload.error) ? payload.error : {};
    // The message comes from the backend already written for a person to
    // read, so it is shown as is rather than mapped by code here.
    return new ApiError (error.code || 'unknown', error.message || Loc ('Something went wrong.'), error);
}

function ParseBody (text)
{
    if (text.length === 0) {
        return null;
    }
    try {
        return JSON.parse (text);
    } catch {
        return undefined;
    }
}

export async function ApiRequest (method, path, body)
{
    let response = null;
    try {
        response = await fetch (ApiPrefix + path, {
            method : method,
            headers : body === undefined ? {} : { 'Content-Type' : 'application/json' },
            body : body === undefined ? undefined : JSON.stringify (body),
            credentials : 'same-origin'
        });
    } catch {
        throw UnavailableError ();
    }

    let payload = ParseBody (await response.text ());
    // Not JSON means the request never reached the backend — most likely
    // there is none behind this host, and Apache answered with a page.
    if (payload === undefined) {
        throw UnavailableError ();
    }
    if (!response.ok) {
        throw ErrorFromPayload (payload);
    }
    return payload;
}

// A handle on an upload that is still going, so it can be stopped. One
// control covers the whole scene: it is handed to every piece in turn, and
// cancelling it stops the piece in flight and everything after it.
export class UploadControl
{
    constructor ()
    {
        this.request = null;
        this.canceled = false;
    }

    Cancel ()
    {
        this.canceled = true;
        if (this.request !== null) {
            this.request.abort ();
        }
    }

    IsCanceled ()
    {
        return this.canceled;
    }
}

// Uploads have to be XMLHttpRequest rather than fetch: fetch reports how much
// of an answer has arrived but says nothing about how much of the body has
// gone out, and an upload with no progress looks like an upload that hung.
export function ApiPutBytes (path, blob, { offset = 0, onProgress = null, control = null } = {})
{
    return new Promise ((resolve, reject) => {
        if (control !== null && control.IsCanceled ()) {
            reject (new ApiError ('aborted', Loc ('Upload cancelled.')));
            return;
        }
        let request = new XMLHttpRequest ();
        request.open ('PUT', ApiPrefix + path, true);
        request.setRequestHeader ('Content-Type', 'application/octet-stream');
        // Where this piece goes. Without it the server would take the body for
        // the whole file and refuse everything after the first piece.
        request.setRequestHeader ('X-Upload-Offset', String (offset));
        request.upload.onprogress = (event) => {
            if (onProgress && event.lengthComputable) {
                onProgress (event.loaded, event.total);
            }
        };
        function Done (settle, value)
        {
            if (control !== null) {
                control.request = null;
            }
            settle (value);
        }
        request.onload = () => {
            let payload = ParseBody (request.responseText || '');
            if (payload === undefined) {
                Done (reject, UnavailableError ());
            } else if (request.status >= 200 && request.status < 300) {
                Done (resolve, payload);
            } else {
                Done (reject, ErrorFromPayload (payload));
            }
        };
        request.onerror = () => Done (reject, UnavailableError ());
        request.onabort = () => Done (reject, new ApiError ('aborted', Loc ('Upload cancelled.')));
        if (control !== null) {
            control.request = request;
        }
        request.send (blob);
    });
}
